import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Card, Typography, Select, Input, InputNumber, Space, Row, Col,
  Descriptions, notification, Popconfirm, Alert, Tag, Tooltip
} from 'antd';
import { SaveOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import billingService from '../../../services/billingService';
import { formatMoney } from '../Orders/orderFormHelpers';
import { formatCreditNoteNumber } from '../../../utils/documentNumber';
import {
  buildLineName, isAssorted, AssortmentBreakdown, componentStemsPerUnit, assortedStemsPerBillingUnit
} from './invoiceLineHelpers';

const { Text, Title } = Typography;
const { TextArea } = Input;

const CREDIT_TYPES = [
  { value: 'QUALITY', label: 'Calidad' },
  { value: 'ROTATION', label: 'Rotación' },
  { value: 'TRANSPORT', label: 'Transporte' },
  { value: 'DISPATCH_ERROR', label: 'Error de despacho' },
  { value: 'OTHER', label: 'Otro' },
];

const UNITS = [
  { value: 'STEM', label: 'Tallos' },
  { value: 'BUNCH', label: 'Ramos' },
  { value: 'BOX', label: 'Cajas' },
];

const BILLING_UNIT_LABEL = { TALLO: 'tallo', RAMO: 'ramo', CAJA: 'caja' };

const CREDIT_TYPE_LABEL = Object.fromEntries(CREDIT_TYPES.map((t) => [t.value, t.label]));

const formatCount = (value) => (Number(value) || 0).toLocaleString('es-CO');

const formatDate = (value) =>
  value ? dayjs(value.slice(0, 10)).format('DD/MM/YYYY') : '-';

// Cuántos tallos vale una unidad en esta línea. Mismo puente que usa el
// backend; acá solo sirve para mostrarle al usuario el valor antes de guardar.
const stemsPerUnit = (unit, line) => {
  if (unit === 'STEM') return 1;
  if (unit === 'BUNCH') return Number(line.stems_per_bunch) || null;
  if (unit === 'BOX') {
    if (!line.stems_per_bunch || !line.bunches_per_box) return null;
    return Number(line.stems_per_bunch) * Number(line.bunches_per_box);
  }
  return null;
};

const BILLING_TO_UNIT = { TALLO: 'STEM', RAMO: 'BUNCH', CAJA: 'BOX' };

// A component is entered in stems or bunches: the box is the whole assortment.
const COMPONENT_UNITS = UNITS.filter((unit) => unit.value !== 'BOX');

// Keys of the `lines` state: invoice_detail_id for a regular line, this for a component.
const componentKey = (component) => `component:${component.component_id}`;

// Lleva la cantidad digitada a la unidad de cobro de la línea.
const conversionFactor = (enteredUnit, line) => {
  const from = stemsPerUnit(enteredUnit, line);
  const to = stemsPerUnit(BILLING_TO_UNIT[line.billing_unit], line);
  if (!from || !to) return null;
  return from / to;
};

const CreditNotesPage = () => {
  const [searchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState(searchParams.get('invoice_id') || null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [reason, setReason] = useState('');
  const [type, setType] = useState('QUALITY');
  // target key -> { quantity, unit }
  const [lines, setLines] = useState({});

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await billingService.listarFacturas({ status: 'ISSUED' });
      setInvoices(response?.data || []);
    } catch (error) {
      notification.error({ message: 'Error', description: 'No se pudieron cargar las facturas.' });
    }
  }, []);

  const fetchInvoice = useCallback(async (id) => {
    if (!id) {
      setInvoice(null);
      return;
    }
    setLoading(true);
    try {
      const response = await billingService.obtenerFactura(id);
      setInvoice(response?.data || null);
      setLines({});
    } catch (error) {
      notification.error({ message: 'Error', description: 'No se pudo cargar la factura.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchInvoice(invoiceId); }, [invoiceId, fetchInvoice]);

  // What a credit can be entered against: a regular line, or each component of
  // an assorted line (an assorted box is credited per component, never whole).
  const targets = (invoice?.details || []).flatMap((line) =>
    isAssorted(line)
      ? line.components.map((component) => ({ key: componentKey(component), line, component }))
      : [{ key: line.invoice_detail_id, line }]
  );

  const updateLine = (key, patch) => {
    setLines((prev) => ({
      ...prev,
      [key]: { unit: 'STEM', quantity: null, ...prev[key], ...patch },
    }));
  };

  // Factor from the entered unit to the line's billing unit. A component is
  // valued per stem: its stems over the stems in one billing unit.
  const targetFactor = (target, unit) => {
    if (!target.component) return conversionFactor(unit, target.line);
    const from = componentStemsPerUnit(unit, target.component);
    const to = assortedStemsPerBillingUnit(target.line);
    if (!from || !to) return null;
    return from / to;
  };

  // Valor de una línea de la baja, en la moneda de la factura.
  const targetValue = (target) => {
    const entry = lines[target.key];
    if (!entry?.quantity) return 0;
    const factor = targetFactor(target, entry.unit);
    if (factor === null) return 0;
    return entry.quantity * factor * Number(target.line.unit_price);
  };

  // Whether the entry asks for more than is left: components compare exact
  // stems, regular lines compare in the billing unit.
  const exceedsAvailable = (target) => {
    const entry = lines[target.key];
    if (!entry?.quantity) return false;
    if (target.component) {
      const stems = componentStemsPerUnit(entry.unit, target.component);
      return stems !== null && entry.quantity * stems > Number(target.component.available_stems);
    }
    const factor = targetFactor(target, entry.unit);
    return factor !== null && entry.quantity * factor > Number(target.line.available) + 1e-6;
  };

  const renderQuantityInput = (target) => (
    <InputNumber
      min={1}
      precision={0}
      style={{ width: '100%' }}
      value={lines[target.key]?.quantity ?? null}
      onChange={(value) => updateLine(target.key, { quantity: value })}
      placeholder="0"
    />
  );

  const renderUnitSelect = (target, options) => (
    <Select
      style={{ width: '100%' }}
      value={lines[target.key]?.unit ?? 'STEM'}
      onChange={(value) => updateLine(target.key, { unit: value })}
      options={options}
    />
  );

  const renderTargetValue = (target) => {
    const entry = lines[target.key];
    if (!entry?.quantity) return <Text type="secondary">-</Text>;

    if (targetFactor(target, entry.unit) === null) {
      return <Text type="danger">Sin empaque</Text>;
    }

    return (
      <Text strong type={exceedsAvailable(target) ? 'danger' : undefined}>
        {formatMoney(targetValue(target), invoice?.currency)}
      </Text>
    );
  };

  const activeTargets = targets.filter((target) => lines[target.key]?.quantity > 0);

  const total = activeTargets.reduce((sum, target) => sum + targetValue(target), 0);

  const excedidas = activeTargets.filter(exceedsAvailable);

  const sinConversion = activeTargets.filter(
    (target) => targetFactor(target, lines[target.key].unit) === null
  );

  const puedeGuardar =
    activeTargets.length > 0 &&
    excedidas.length === 0 &&
    sinConversion.length === 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await billingService.crearBaja({
        invoice_id: invoiceId,
        reason: reason.trim(),
        type,
        details: activeTargets.map((target) => ({
          invoice_detail_id: target.line.invoice_detail_id,
          ...(target.component ? { component_id: target.component.component_id } : {}),
          quantity_entered: lines[target.key].quantity,
          unit_entered: lines[target.key].unit,
        })),
      });
      notification.success({
        message: `Baja ${formatCreditNoteNumber(response?.data?.number)} registrada`,
        description: `Se acreditaron ${formatMoney(response?.data?.total, invoice?.currency)}.`,
      });
      setReason('');
      setLines({});
      await Promise.all([fetchInvoice(invoiceId), fetchInvoices()]);
    } catch (error) {
      notification.error({
        message: 'No se pudo registrar la baja',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVoidCredit = async (note) => {
    try {
      await billingService.anularBaja(note.credit_note_id);
      notification.success({
        message: `Baja ${formatCreditNoteNumber(note.number)} anulada`,
        description: `Se devolvieron ${formatMoney(note.total, invoice?.currency)} al saldo de la factura.`,
      });
      await Promise.all([fetchInvoice(invoiceId), fetchInvoices()]);
    } catch (error) {
      notification.error({
        message: 'No se pudo anular la baja',
        description: error?.response?.data?.mensaje || 'Error inesperado.',
      });
    }
  };

  const creditNoteColumns = [
    {
      title: 'Nº',
      dataIndex: 'number',
      key: 'number',
      render: (value) => <Text strong>{formatCreditNoteNumber(value)}</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      render: formatDate,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (value) => CREDIT_TYPE_LABEL[value] || value,
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => value || <Text type="secondary">-</Text>,
    },
    {
      title: 'Registrada por',
      key: 'user',
      render: (_, record) => record.user?.full_name || '-',
    },
    {
      title: 'Valor',
      key: 'total',
      align: 'right',
      render: (_, record) => formatMoney(record.total, invoice?.currency),
    },
    {
      title: 'Estado',
      key: 'status',
      render: (_, record) =>
        record.status === 'ACTIVE' ? (
          <Tag color="success">Activa</Tag>
        ) : (
          <Tooltip
            title={`Anulada${record.voider ? ` por ${record.voider.full_name}` : ''}${
              record.voided_at ? ` el ${dayjs(record.voided_at).format('DD/MM/YYYY HH:mm')}` : ''}`}
          >
            <Tag>Anulada</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) =>
        record.status === 'ACTIVE' && (
          <Popconfirm
            title="Anular baja"
            description={`¿Anular la baja ${formatCreditNoteNumber(record.number)} de ${formatMoney(record.total, invoice?.currency)}? El valor vuelve al saldo de la factura.`}
            okText="Anular"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleVoidCredit(record)}
          >
            <Tooltip title="Anular baja">
              <Button size="small" danger icon={<StopOutlined />} />
            </Tooltip>
          </Popconfirm>
        ),
    },
  ];

  const columns = [
    {
      title: 'Producto',
      key: 'product',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{buildLineName(record)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.product_sku}</Text>
        </Space>
      ),
    },
    {
      title: 'Facturado',
      key: 'invoiced',
      align: 'right',
      render: (_, record) => (
        <Text>
          {record.invoiced} {BILLING_UNIT_LABEL[record.billing_unit]}(s)
        </Text>
      ),
    },
    {
      title: 'Acreditado',
      key: 'credited',
      align: 'right',
      render: (_, record) =>
        Number(record.credited) > 0
          ? <Text type="danger">{record.credited}</Text>
          : <Text type="secondary">-</Text>,
    },
    {
      title: 'Disponible',
      key: 'available',
      align: 'right',
      render: (_, record) => <Text strong>{record.available}</Text>,
    },
    {
      title: 'Cantidad a bajar',
      key: 'quantity',
      width: 140,
      render: (_, record) =>
        isAssorted(record)
          ? <Text type="secondary">Por componente</Text>
          : renderQuantityInput({ key: record.invoice_detail_id, line: record }),
    },
    {
      title: 'Unidad',
      key: 'unit',
      width: 130,
      render: (_, record) =>
        isAssorted(record) ? null : renderUnitSelect({ key: record.invoice_detail_id, line: record }, UNITS),
    },
    {
      title: 'Valor',
      key: 'value',
      align: 'right',
      render: (_, record) => {
        if (!isAssorted(record)) return renderTargetValue({ key: record.invoice_detail_id, line: record });

        // An assorted line adds up what its components credit.
        const sum = targets
          .filter((target) => target.line === record)
          .reduce((acc, target) => acc + targetValue(target), 0);
        return sum > 0
          ? <Text strong>{formatMoney(sum, invoice?.currency)}</Text>
          : <Text type="secondary">-</Text>;
      },
    },
  ];

  // Credit inputs appended to the recipe of an assorted line; quantities in stems.
  const componentCreditColumns = (line) => [
    {
      title: 'Facturado',
      key: 'invoiced_stems',
      align: 'right',
      render: (_, component) => `${formatCount(component.invoiced_stems)} tallos`,
    },
    {
      title: 'Acreditado',
      key: 'credited_stems',
      align: 'right',
      render: (_, component) =>
        Number(component.credited_stems) > 0
          ? <Text type="danger">{formatCount(component.credited_stems)}</Text>
          : <Text type="secondary">-</Text>,
    },
    {
      title: 'Disponible',
      key: 'available_stems',
      align: 'right',
      render: (_, component) => <Text strong>{formatCount(component.available_stems)}</Text>,
    },
    {
      title: 'Cantidad a bajar',
      key: 'quantity',
      width: 140,
      render: (_, component) => renderQuantityInput({ key: componentKey(component), line, component }),
    },
    {
      title: 'Unidad',
      key: 'unit',
      width: 130,
      render: (_, component) =>
        renderUnitSelect({ key: componentKey(component), line, component }, COMPONENT_UNITS),
    },
    {
      title: 'Valor',
      key: 'value',
      align: 'right',
      render: (_, component) => renderTargetValue({ key: componentKey(component), line, component }),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0, color: '#595959', fontWeight: 600 }}>
            Bajas
          </Title>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={10}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Factura
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Selecciona una factura"
              value={invoiceId}
              onChange={setInvoiceId}
              showSearch
              optionFilterProp="label"
              options={invoices.map((inv) => ({
                value: inv.invoice_id,
                label: `${inv.display_number} ·${inv.client?.name || ''} · saldo ${formatMoney(inv.balance, inv.currency)}`,
              }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Tipo de baja
            </Text>
            <Select style={{ width: '100%' }} value={type} onChange={setType} options={CREDIT_TYPES} />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Motivo
            </Text>
            <TextArea
              rows={1}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Opcional"
              maxLength={300}
            />
          </Col>
          <Col xs={24} md={2}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchInvoice(invoiceId)}
              disabled={!invoiceId}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {invoice && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label="Cliente">{invoice.client?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Fecha">
              {invoice.date ? dayjs(invoice.date.slice(0, 10)).format('DD/MM/YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Total">
              {formatMoney(invoice.total, invoice.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Saldo">
              <Text strong>{formatMoney(invoice.balance, invoice.currency)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {excedidas.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Hay líneas que superan lo facturado"
          description="No se puede acreditar más de lo disponible en una línea. Ajusta las cantidades marcadas en rojo."
        />
      )}

      {sinConversion.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Unidad no convertible"
          description="Alguna línea no tiene el empaque necesario para la unidad elegida. Cambia la unidad."
        />
      )}

      {/* Keyed by invoice so it remounts per invoice and opens every assorted
          line: that is where its credit is entered. */}
      <Table
        key={invoice?.invoice_id || 'empty'}
        columns={columns}
        dataSource={invoice?.details || []}
        rowKey={(record) => record.invoice_detail_id}
        loading={loading}
        scroll={{ x: 1000 }}
        expandable={{
          rowExpandable: isAssorted,
          defaultExpandAllRows: true,
          expandedRowRender: (record) => (
            <AssortmentBreakdown components={record.components} extraColumns={componentCreditColumns(record)} />
          ),
        }}
        pagination={false}
        locale={{ emptyText: 'Selecciona una factura para ver sus líneas' }}
      />

      {invoice && (
        <Row justify="end" align="middle" style={{ marginTop: 16 }}>
          <Space size="large">
            <Text>
              Total de la baja: <Text strong>{formatMoney(total, invoice.currency)}</Text>
            </Text>
            <Popconfirm
              title="Registrar baja"
              description={`Se acreditarán ${formatMoney(total, invoice.currency)} sobre la factura ${invoice.display_number}.`}
              okText="Registrar"
              cancelText="Cancelar"
              onConfirm={handleSave}
              disabled={!puedeGuardar}
            >
              <Button
                type="primary"
                icon={<SaveOutlined />}
                disabled={!puedeGuardar}
                loading={saving}
                style={{ backgroundColor: 'var(--fc-accent)' }}
              >
                Registrar baja
              </Button>
            </Popconfirm>
          </Space>
        </Row>
      )}

      {invoice && (
        <Card title="Bajas registradas" style={{ marginTop: 24 }}>
          <Table
            columns={creditNoteColumns}
            dataSource={invoice.credit_notes || []}
            rowKey={(record) => record.credit_note_id}
            scroll={{ x: 900 }}
            pagination={false}
            locale={{ emptyText: 'Esta factura no tiene bajas' }}
          />
        </Card>
      )}
    </div>
  );
};

export default CreditNotesPage;
